import {useState} from "react";
import {ClientContext, ClientContextId} from "common/types/client_context";

export const maxContextLength = 30000;

export type PresetOption = ClientContext | {inputValue: string; name: string};

export interface ClientContextEditor {
  editedContext: string;
  setEditedContext: (context: string) => void;
  inputValue: string;
  setInputValue: (value: string) => void;
  presets: ClientContext[];
  selectedPreset: ClientContext | null;
  pickerError: string;
  typedName: string;
  isOverLimit: boolean;
  reset: (context: string, clientContextId: ClientContextId | null) => void;
  handlePickerChange: (value: PresetOption | string | null) => void;
  commit: () => Promise<{clientContextId: ClientContextId | null} | null>;
  hasChangedFrom: (
    context: string,
    clientContextId: ClientContextId | null,
  ) => boolean;
}

export function useClientContextEditor(
  presets: ClientContext[],
  createClientContext: (
    name: string,
    context: string,
  ) => Promise<ClientContext>,
): ClientContextEditor {
  const [editedContext, setEditedContext] = useState("");
  const [selectedId, setSelectedId] = useState<ClientContextId | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [pickerError, setPickerError] = useState("");

  const selectedPreset = presets.find(p => p.id === selectedId) ?? null;
  const typedName = inputValue.trim();

  const reset = (context: string, clientContextId: ClientContextId | null) => {
    setEditedContext(context ?? "");
    setSelectedId(clientContextId);
    const linked = clientContextId
      ? presets.find(preset => preset.id === clientContextId)
      : null;
    setInputValue(linked ? linked.name : "");
    setPickerError("");
  };

  const handleSelectPreset = (preset: ClientContext | null) => {
    setSelectedId(preset ? preset.id : null);
    if (preset) {
      setEditedContext(preset.context);
    }
  };

  const handleCreatePreset = async (
    name: string,
  ): Promise<ClientContext | null> => {
    try {
      const created = await createClientContext(name, editedContext);
      setSelectedId(created.id);
      setPickerError("");
      return created;
    } catch {
      setPickerError(`Could not create "${name}"`);
      return null;
    }
  };

  const startNewPreset = (name: string) => {
    if (selectedPreset && editedContext === selectedPreset.context) {
      setEditedContext("");
    }
    setSelectedId(null);
    setInputValue(name);
  };

  const handlePickerChange = (value: PresetOption | string | null) => {
    setPickerError("");
    if (typeof value === "string") {
      const existing = presets.find(preset => preset.name === value.trim());
      if (existing) {
        handleSelectPreset(existing);
      } else {
        startNewPreset(value);
      }
    } else if (value && "inputValue" in value) {
      startNewPreset(value.inputValue);
    } else {
      handleSelectPreset(value);
    }
  };

  const commit = async (): Promise<{
    clientContextId: ClientContextId | null;
  } | null> => {
    let contextId = selectedId;
    if (typedName && typedName !== selectedPreset?.name) {
      const existing = presets.find(preset => preset.name === typedName);
      if (existing) {
        contextId = existing.id;
      } else {
        const created = await handleCreatePreset(typedName);
        if (!created) {
          return null;
        }
        contextId = created.id;
      }
    }
    return {clientContextId: contextId};
  };

  const hasChangedFrom = (
    context: string,
    clientContextId: ClientContextId | null,
  ) =>
    editedContext !== (context ?? "") ||
    selectedId !== clientContextId ||
    (typedName !== "" && typedName !== (selectedPreset?.name ?? ""));

  return {
    editedContext,
    setEditedContext,
    inputValue,
    setInputValue,
    presets,
    selectedPreset,
    pickerError,
    typedName,
    isOverLimit: editedContext.length > maxContextLength,
    reset,
    handlePickerChange,
    commit,
    hasChangedFrom,
  };
}
